import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar, Clock, Link2, Settings, CheckCircle2, XCircle, Copy, ExternalLink, Video, Phone, Users, Trash2, Send, Brain, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { format } from 'date-fns';

const meetingAPI = {
  getGoogleStatus: () => api.get('/api/meetings/google/status'),
  getGoogleConnect: () => api.get('/api/meetings/google/connect'),
  disconnectGoogle: () => api.post('/api/meetings/google/disconnect'),
  getTypes: () => api.get('/api/meetings/types'),
  updateTypes: (types) => api.put('/api/meetings/types', types),
  getWorkingHours: () => api.get('/api/meetings/working-hours'),
  updateWorkingHours: (hours) => api.put('/api/meetings/working-hours', hours),
  getBookingLink: () => api.get('/api/meetings/booking-link'),
  getUpcoming: (limit = 20) => api.get(`/api/meetings/upcoming?limit=${limit}`),
  getAllMeetings: (status) => api.get(`/api/meetings/all${status ? `?status=${status}` : ''}`),
  cancelMeeting: (id) => api.post(`/api/meetings/${id}/cancel`),
  recordOutcome: (id, outcome) => api.post(`/api/meetings/${id}/outcome`, { outcome }),
  repairAutoNoShows: (apply) => api.post(`/api/meetings/repair-auto-noshows?apply=${apply}`),
  generateFollowUp: (id) => api.post(`/api/meetings/${id}/follow-up`),
  sendReminder: (id) => api.post(`/api/meetings/${id}/reminder`),
  getIntentHistory: () => api.get('/api/meetings/intent/history'),
  analyzeLeadIntent: (leadId) => api.post(`/api/meetings/intent/analyze/${leadId}`),
};

const typeIcons = {
  discovery: <Phone className="h-4 w-4" />,
  demo: <Video className="h-4 w-4" />,
  strategy: <Users className="h-4 w-4" />,
};

export default function MeetingScheduler() {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [googleStatus, setGoogleStatus] = useState(null);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [workingHours, setWorkingHours] = useState(null);
  const [bookingLink, setBookingLink] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [allMeetings, setAllMeetings] = useState([]);
  const [intentHistory, setIntentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle OAuth redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      toast.error(`Google Calendar connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, typesRes, hoursRes, linkRes, upcomingRes, allRes, intentRes] = await Promise.all([
        meetingAPI.getGoogleStatus(),
        meetingAPI.getTypes(),
        meetingAPI.getWorkingHours(),
        meetingAPI.getBookingLink(),
        meetingAPI.getUpcoming(),
        meetingAPI.getAllMeetings(),
        meetingAPI.getIntentHistory(),
      ]);
      setGoogleStatus(statusRes.data);
      setMeetingTypes(typesRes.data.meeting_types || []);
      setWorkingHours(hoursRes.data);
      setBookingLink(linkRes.data.booking_link || '');
      setTenantId(linkRes.data.tenant_id || '');
      setUpcomingMeetings(upcomingRes.data.meetings || []);
      setAllMeetings(allRes.data.meetings || []);
      setIntentHistory(intentRes.data.detections || []);
    } catch (err) {
      console.error('Failed to load meeting scheduler data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const connectGoogle = async () => {
    try {
      const res = await meetingAPI.getGoogleConnect();
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error('Failed to start Google Calendar connection');
    }
  };

  const disconnectGoogle = async () => {
    try {
      await meetingAPI.disconnectGoogle();
      setGoogleStatus({ connected: false });
      toast.success('Google Calendar disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const copyBookingLink = () => {
    navigator.clipboard.writeText(bookingLink);
    toast.success('Booking link copied to clipboard');
  };

  const toggleMeetingType = async (typeId) => {
    const updated = meetingTypes.map(t =>
      t.id === typeId ? { ...t, is_active: !t.is_active } : t
    );
    setMeetingTypes(updated);
    try {
      await meetingAPI.updateTypes(updated);
      toast.success('Meeting type updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleCancel = async (meetingId) => {
    try {
      await meetingAPI.cancelMeeting(meetingId);
      toast.success('Meeting cancelled');
      loadData();
    } catch { toast.error('Failed to cancel'); }
  };

  const handleFollowUp = async (meetingId) => {
    try {
      const res = await meetingAPI.generateFollowUp(meetingId);
      toast.success(`Follow-up email drafted: ${res.data.subject}`);
    } catch { toast.error('Failed to generate follow-up'); }
  };

  const handleReminder = async (meetingId) => {
    try {
      await meetingAPI.sendReminder(meetingId);
      toast.success('Reminder sent');
    } catch { toast.error('Failed to send reminder'); }
  };

  const handleOutcome = async (meetingId, outcome) => {
    const labels = { held: 'Held', no_show: 'No-show', rescheduled: 'Rescheduled' };
    try {
      await meetingAPI.recordOutcome(meetingId, outcome);
      toast.success(`Meeting marked as ${labels[outcome] || outcome}`);
      loadData();
    } catch { toast.error('Failed to record outcome'); }
  };

  const handleRepairNoShows = async () => {
    try {
      const dry = await meetingAPI.repairAutoNoShows(false);
      const n = dry.data?.matched || 0;
      if (!n) { toast.info('No auto-flagged no-shows to reset.'); return; }
      if (!window.confirm(`${n} meeting(s) were auto-marked as no-show by the system (never confirmed). Reset them to "awaiting outcome" so you can record what actually happened?`)) return;
      await meetingAPI.repairAutoNoShows(true);
      toast.success(`Reset ${n} auto-flagged no-show${n === 1 ? '' : 's'} — record their real outcomes below.`);
      loadData();
    } catch { toast.error('Failed to reset no-shows'); }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="meeting-scheduler-loading">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-7 w-52 bg-muted rounded animate-pulse" />
            <div className="h-4 w-72 bg-muted/60 rounded animate-pulse" />
          </div>
          <div className="h-9 w-28 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-2">
              <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-lg border p-6 space-y-3">
          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
          <div className="h-40 w-full bg-muted/20 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="meeting-scheduler-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Meeting Scheduler</h1>
          <p className="text-muted-foreground mt-1">Auto-insert booking links in cadence emails. Brain-driven smart timing.</p>
        </div>
        <div className="flex items-center gap-3">
          {bookingLink && (
            <Button variant="outline" size="sm" onClick={copyBookingLink} data-testid="copy-booking-link-btn">
              <Copy className="h-4 w-4 mr-2" /> Copy Booking Link
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.open(`/book/${tenantId}`, '_blank')} data-testid="preview-booking-btn">
            <ExternalLink className="h-4 w-4 mr-2" /> Preview Booking Page
          </Button>
        </div>
      </div>

      {/* Google Calendar Connection */}
      <Card data-testid="google-calendar-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Google Calendar</p>
                {googleStatus?.connected ? (
                  <p className="text-sm text-muted-foreground">Connected as {googleStatus.email}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Connect to sync meetings & check availability</p>
                )}
              </div>
            </div>
            {googleStatus?.connected ? (
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
                </Badge>
                <Button variant="ghost" size="sm" onClick={disconnectGoogle} data-testid="disconnect-google-btn">
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button onClick={connectGoogle} data-testid="connect-google-btn">
                <Calendar className="h-4 w-4 mr-2" /> Connect Google Calendar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="meeting-scheduler-tabs">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            <CalendarCheck className="h-4 w-4 mr-2" /> Upcoming ({upcomingMeetings.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-meetings">
            <Calendar className="h-4 w-4 mr-2" /> All Meetings
          </TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-meeting-types">
            <Settings className="h-4 w-4 mr-2" /> Meeting Types
          </TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-working-hours">
            <Clock className="h-4 w-4 mr-2" /> Working Hours
          </TabsTrigger>
          <TabsTrigger value="intent" data-testid="tab-intent">
            <Brain className="h-4 w-4 mr-2" /> Auto-Detection ({intentHistory.filter(d => d.has_intent).length})
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Meetings */}
        <TabsContent value="upcoming" className="mt-6">
          <UpcomingMeetingsTab
            meetings={upcomingMeetings}
            onCancel={handleCancel}
            onFollowUp={handleFollowUp}
            onReminder={handleReminder}
          />
        </TabsContent>

        {/* All Meetings */}
        <TabsContent value="all" className="mt-6">
          <AllMeetingsTab meetings={allMeetings} onCancel={handleCancel} onFollowUp={handleFollowUp} onOutcome={handleOutcome} onRepair={handleRepairNoShows} />
        </TabsContent>

        {/* Meeting Types */}
        <TabsContent value="types" className="mt-6">
          <MeetingTypesTab types={meetingTypes} onToggle={toggleMeetingType} />
        </TabsContent>

        {/* Working Hours */}
        <TabsContent value="hours" className="mt-6">
          <WorkingHoursTab hours={workingHours} onSave={async (h) => {
            try {
              await meetingAPI.updateWorkingHours(h);
              setWorkingHours(h);
              toast.success('Working hours updated');
            } catch { toast.error('Failed to update'); }
          }} />
        </TabsContent>

        {/* Intent Detection */}
        <TabsContent value="intent" className="mt-6">
          <IntentHistoryTab detections={intentHistory} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UpcomingMeetingsTab({ meetings, onCancel, onFollowUp, onReminder }) {
  if (!meetings.length) {
    return (
      <Card>
        <CardContent className="pt-12 pb-12 text-center">
          <CalendarCheck className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="text-lg font-medium">No upcoming meetings</p>
          <p className="text-muted-foreground mt-1">Share your booking link with leads or let the AI brain schedule meetings automatically.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="upcoming-meetings-list">
      {meetings.map(m => (
        <Card key={m.id} data-testid={`meeting-card-${m.id}`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${meetingTypeColor(m.meeting_type_id)}15`, color: meetingTypeColor(m.meeting_type_id) }}>
                  {typeIcons[m.meeting_type_id] || <Calendar className="h-4 w-4" />}
                </div>
                <div>
                  <p className="font-medium">{m.meeting_type_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {m.booker_name} {m.lead_company && `(${m.lead_company})`} &middot; {format(new Date(m.start_time), 'MMM d, yyyy h:mm a')} &middot; {m.duration_minutes}min
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => onReminder(m.id)} data-testid={`reminder-btn-${m.id}`}>
                  <Send className="h-3 w-3 mr-1" /> Remind
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onCancel(m.id)} data-testid={`cancel-btn-${m.id}`}>
                  <XCircle className="h-3 w-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AllMeetingsTab({ meetings, onCancel, onFollowUp, onOutcome, onRepair }) {
  const statusColors = {
    confirmed: 'bg-green-50 text-green-700 border-green-200',
    scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
    proposed: 'bg-amber-50 text-amber-700 border-amber-200',
    expired: 'bg-gray-100 text-gray-600 border-gray-300',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    held: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    no_show: 'bg-orange-50 text-orange-700 border-orange-200',
    awaiting_outcome: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const statusLabels = { awaiting_outcome: 'awaiting outcome', completed: 'held', no_show: 'no-show', proposed: 'awaiting confirmation' };
  const now = Date.now();
  const isPast = (m) => m.start_time && new Date(m.start_time).getTime() < now;
  const needsOutcome = (m) => m.status === 'awaiting_outcome' || (isPast(m) && ['confirmed', 'scheduled'].includes(m.status));
  const autoNoShows = meetings.filter(m => m.status === 'no_show' && m.no_show_at && !m.outcome_recorded_by).length;

  return (
    <Card data-testid="all-meetings-table">
      <CardContent className="pt-4">
        {autoNoShows > 0 && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="auto-noshow-banner">
            <p className="text-sm text-amber-800">
              <strong>{autoNoShows}</strong> meeting{autoNoShows === 1 ? ' was' : 's were'} auto-marked as no-show by the system and never confirmed. This inflates your no-show rate.
            </p>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0" onClick={onRepair} data-testid="repair-noshows-btn">
              Reset & record real outcomes
            </Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Meeting</TableHead>
              <TableHead>Attendee</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {meetings.map(m => (
              <TableRow key={m.id} data-testid={`all-meeting-row-${m.id}`}>
                <TableCell className="font-medium">{m.meeting_type_name}</TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{m.booker_name}</p>
                    <p className="text-xs text-muted-foreground">{m.booker_email}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {m.start_time ? format(new Date(m.start_time), 'MMM d, yyyy h:mm a') : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusColors[m.status] || ''} data-testid={`meeting-status-${m.id}`}>
                    {statusLabels[m.status] || m.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {needsOutcome(m) ? (
                    <div className="flex flex-wrap gap-1" data-testid={`outcome-actions-${m.id}`}>
                      <Button variant="outline" size="sm" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => onOutcome(m.id, 'held')} data-testid={`outcome-held-${m.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Held
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-orange-700 border-orange-200 hover:bg-orange-50"
                        onClick={() => onOutcome(m.id, 'no_show')} data-testid={`outcome-noshow-${m.id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> No-show
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-blue-700 border-blue-200 hover:bg-blue-50"
                        onClick={() => onOutcome(m.id, 'rescheduled')} data-testid={`outcome-reschedule-${m.id}`}>
                        <Clock className="h-3 w-3 mr-1" /> Rescheduled
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      {m.status === 'confirmed' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => onFollowUp(m.id)} data-testid={`followup-btn-${m.id}`}>
                            <Brain className="h-3 w-3 mr-1" /> Follow-up
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => onCancel(m.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!meetings.length && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No meetings yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function MeetingTypesTab({ types, onToggle }) {
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="meeting-types-grid">
      {types.map(t => (
        <Card key={t.id} className={!t.is_active ? 'opacity-50' : ''} data-testid={`meeting-type-${t.id}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${t.color}15`, color: t.color }}>
                {typeIcons[t.id] || <Calendar className="h-5 w-5" />}
              </div>
              <Switch checked={t.is_active} onCheckedChange={() => onToggle(t.id)} data-testid={`toggle-${t.id}`} />
            </div>
            <h3 className="font-semibold">{t.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
            <Badge variant="outline" className="mt-3">
              <Clock className="h-3 w-3 mr-1" /> {t.duration_minutes} minutes
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function WorkingHoursTab({ hours, onSave }) {
  const [localHours, setLocalHours] = useState(hours);
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => { setLocalHours(hours); }, [hours]);

  if (!localHours) return null;

  const toggleDay = (day) => {
    setLocalHours(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: { ...prev.days[day], enabled: !prev.days[day].enabled },
      },
    }));
  };

  const updateTime = (day, field, value) => {
    setLocalHours(prev => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: { ...prev.days[day], [field]: value },
      },
    }));
  };

  return (
    <Card data-testid="working-hours-config">
      <CardHeader>
        <CardTitle className="text-lg">Working Hours</CardTitle>
        <CardDescription>Set your availability for meeting bookings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <Label>Timezone</Label>
          <Select value={localHours.timezone} onValueChange={(v) => setLocalHours(prev => ({ ...prev, timezone: v }))}>
            <SelectTrigger className="w-[240px]" data-testid="timezone-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
              <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
              <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
              <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
              <SelectItem value="UTC">UTC</SelectItem>
            </SelectContent>
          </Select>
          <Label className="ml-4">Buffer between meetings</Label>
          <Select value={String(localHours.buffer_minutes)} onValueChange={(v) => setLocalHours(prev => ({ ...prev, buffer_minutes: parseInt(v) }))}>
            <SelectTrigger className="w-[120px]" data-testid="buffer-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No buffer</SelectItem>
              <SelectItem value="5">5 min</SelectItem>
              <SelectItem value="10">10 min</SelectItem>
              <SelectItem value="15">15 min</SelectItem>
              <SelectItem value="30">30 min</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {days.map(day => (
            <div key={day} className="flex items-center gap-4 py-2 border-b last:border-0">
              <Switch
                checked={localHours.days[day]?.enabled}
                onCheckedChange={() => toggleDay(day)}
                data-testid={`day-toggle-${day}`}
              />
              <span className="w-24 font-medium capitalize">{day}</span>
              {localHours.days[day]?.enabled ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={localHours.days[day]?.start || '09:00'}
                    onChange={(e) => updateTime(day, 'start', e.target.value)}
                    className="w-32"
                    data-testid={`start-time-${day}`}
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={localHours.days[day]?.end || '18:00'}
                    onChange={(e) => updateTime(day, 'end', e.target.value)}
                    className="w-32"
                    data-testid={`end-time-${day}`}
                  />
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Unavailable</span>
              )}
            </div>
          ))}
        </div>

        <Button onClick={() => onSave(localHours)} className="mt-4" data-testid="save-hours-btn">
          Save Working Hours
        </Button>
      </CardContent>
    </Card>
  );
}

function meetingTypeColor(typeId) {
  const colors = { discovery: '#3B82F6', demo: '#8B5CF6', strategy: '#059669' };
  return colors[typeId] || '#6B7280';
}

function IntentHistoryTab({ detections }) {
  const intentColors = {
    meeting_request: 'bg-green-50 text-green-700 border-green-200',
    demo_interest: 'bg-blue-50 text-blue-700 border-blue-200',
    pricing_inquiry: 'bg-purple-50 text-purple-700 border-purple-200',
    implementation_discussion: 'bg-amber-50 text-amber-700 border-amber-200',
    general_interest: 'bg-muted text-muted-foreground border-border',
    none: 'bg-muted text-muted-foreground border-border',
  };

  const actionColors = {
    booking_email_sent: 'bg-green-50 text-green-700 border-green-200',
    booking_email_drafted: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };

  if (!detections.length) {
    return (
      <Card>
        <CardContent className="pt-12 pb-12 text-center">
          <Brain className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="text-lg font-medium">No intent detections yet</p>
          <p className="text-muted-foreground mt-1">The AI brain will automatically detect buying intent from call transcripts, email replies, and inbound leads.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="intent-history-table">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Brain className="h-5 w-5" /> Auto-Detection History
        </CardTitle>
        <CardDescription>The AI brain analyzes call transcripts, email replies, and inbound leads for buying intent, then autonomously sends booking link emails.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lead</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Intent</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Reasoning</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detections.map(d => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.lead_name || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {d.source?.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={intentColors[d.intent_type] || ''}>
                    {d.intent_type?.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(d.confidence || 0) * 100}%`,
                          backgroundColor: d.confidence >= 0.7 ? '#22c55e' : d.confidence >= 0.4 ? '#eab308' : '#ef4444',
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">{((d.confidence || 0) * 100).toFixed(0)}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                  {d.reasoning}
                </TableCell>
                <TableCell>
                  {d.action_taken ? (
                    <Badge variant="outline" className={actionColors[d.action_taken] || ''}>
                      {d.action_taken === 'booking_email_sent' ? 'Email Sent' : 'Drafted'}
                    </Badge>
                  ) : d.has_intent ? (
                    <span className="text-xs text-muted-foreground">Pending</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">No action</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
